import parseJsonApiResponse from '@/api/jsonapi/ResponseParser';
import AxiosRestService from '@gms/network/src/axiosRestService';
import Vue from 'vue';

const initialState = () => ({
  loading: false,
  activeAndFutureMaintenancePeriods: [],
  errorGetMaintenancePeriods: false,
  affectedReservations: [],
  affectedReservationsCount: 0,
  errorGetAffectedReservations: false,
})

export default {
  resource: 'maintenancePeriods',
  namespaced: true,
  state: initialState(),
  getters: {
    loading: (state) => state.loading,
    activeAndFutureMaintenancePeriods: (state) => state.activeAndFutureMaintenancePeriods,
    activePeriods: (state) => {
      const now = (new Date()).toISOString()
      return state.activeAndFutureMaintenancePeriods
        .filter((period) => period.start < now
          && (period.end === undefined || period.end === null || period.end > now))
    },
    futurePeriods: (state) => {
      const now = (new Date()).toISOString()
      return state.activeAndFutureMaintenancePeriods.filter((period) => period.start > now)
    },
    errorGetMaintenancePeriods: (state) => state.errorGetMaintenancePeriods,
    affectedReservations: (state) => state.affectedReservations,
    affectedReservationsCount: (state) => state.affectedReservationsCount,
    errorGetAffectedReservations: (state) => state.errorGetAffectedReservations,
  },
  mutations: {
    setLoading: (state, loading) => Vue.set(state, 'loading', loading),
    setActiveAndFutureMaintenancePeriods: (state, periods) => Vue.set(state, 'activeAndFutureMaintenancePeriods', periods),
    setErrorGetMaintenancePeriods: (state, error) => Vue.set(state, 'errorGetMaintenancePeriods', error),
    setAffectedReservations: (state, reservations) => Vue.set(state, 'affectedReservations', reservations),
    setAffectedReservationsCount: (state, count) => Vue.set(state, 'affectedReservationsCount', count),
    setErrorGetAffectedReservations: (state, error) => Vue.set(state, 'errorGetAffectedReservations', error),
    reset(state) {
      const newState = initialState()
      Object.keys(newState).forEach((key) => {
        state[key] = newState[key]
      })
    },
  },
  actions: {
    fetchActiveAndFuture: (context) => {
      context.commit('setLoading', true)
      const currentTime = new Date().toISOString();
      const params = {
        'filter[end][since]': currentTime,
        'filter[revoked]': false,
        sort: '-start',
        include: 'Devices,Zones,Sites',
      };

      return AxiosRestService
        .get(
          '/maintenance-periods',
          {
            headers: {
              Accept: 'application/vnd.api+json',
            },
            params,
          },
        )
        .then((response) => {
          context.commit('setActiveAndFutureMaintenancePeriods', parseJsonApiResponse(response))
          context.commit('setLoading', false)
        })
        .catch(() => {
          context.commit('setActiveAndFutureMaintenancePeriods', [])
          context.commit('setLoading', false)
        })
    },
    fetchAffectedReservations: (context, {
      item,
      deviceId,
      siteId,
      zoneId,
    }) => {
      context.commit('setLoading', true)
      context.commit('setErrorGetAffectedReservations', false)

      const params = {
        'filter[deviceId]': deviceId,
        'filter[siteId]': siteId,
        'filter[zoneId]': zoneId,
        'filter[start][lt]': item.end,
        'filter[end][gt]': item.start,
        'filter[state]': 'RESERVED',
        sort: 'start',
        include: 'Devices,Users,Sites.Locations',
      }
      return AxiosRestService
        .get(
          '/reservations',
          {
            headers: {
              Accept: 'application/vnd.api+json',
            },
            params,
          },
        )
        .then((response) => {
          context.commit('setAffectedReservations', parseJsonApiResponse(response))
          context.commit('setAffectedReservationsCount', response.data.meta.record_count)
          context.commit('setLoading', false)
        })
        .catch(() => {
          context.commit('setAffectedReservations', [])
          context.commit('setAffectedReservationsCount', 0)
          context.commit('setErrorGetAffectedReservations', true)
          context.commit('setLoading', false)
        })
    },
    addMaintenancePeriod: (context, {
      item,
      deviceId,
      siteId,
      zoneId,
    }) => {
      context.commit('setLoading', true)
      context.commit('setErrorGetMaintenancePeriods', false)

      const payload = {
        data: {
          type: 'maintenancePeriods',
          attributes: {
            name: item.name,
            start: item.start,
            end: item.end,
            description: item.description,
            reason: item.reason,
          },
          relationships: {
            devices: deviceId ? { data: [{ type: 'devices', id: deviceId }] } : undefined,
            sites: siteId ? { data: [{ type: 'sites', id: siteId }] } : undefined,
            zones: zoneId ? { data: [{ type: 'zones', id: zoneId }] } : undefined,
          },
        },
      };

      const acceptedLanguage = AxiosRestService.resetAcceptedLanguage()
      const contentLanguage = AxiosRestService.resetContentLanguage()

      const postResult = AxiosRestService.post(
        '/maintenance-periods',
        payload,
        {
          headers: {
            Accept: 'application/vnd.api+json',
            'Content-Type': 'application/vnd.api+json',
          },
        },
      )
        .then((response) => {
          context.commit('setLoading', false)
        })
        .catch(() => {
          context.commit('setErrorGetMaintenancePeriods', true)
          context.commit('setLoading', false)
        })

      AxiosRestService.setAcceptedLanguage(acceptedLanguage)
      AxiosRestService.setContentLanguage(contentLanguage)

      return postResult
    },
    updateMaintenancePeriod: (context, {
      item,
    }) => {
      context.commit('setLoading', true)
      context.commit('setErrorGetMaintenancePeriods', false)

      const payload = {
        data: {
          type: 'maintenancePeriods',
          id: item.apiId,
          attributes: {
            name: item.name,
            start: item.start,
            end: item.end,
            description: item.description,
            reason: item.reason,
          },
        },
      };

      const acceptedLanguage = AxiosRestService.resetAcceptedLanguage()
      const contentLanguage = AxiosRestService.resetContentLanguage()

      const patchResult = AxiosRestService.patch(
        `/maintenance-periods/${item.apiId}`,
        payload,
        {
          headers: {
            Accept: 'application/vnd.api+json',
            'Content-Type': 'application/vnd.api+json',
          },
        },
      )
        .then((response) => {
          context.commit('setLoading', false)
        })
        .catch(() => {
          context.commit('setErrorGetMaintenancePeriods', true)
          context.commit('setLoading', false)
        })

      AxiosRestService.setAcceptedLanguage(acceptedLanguage)
      AxiosRestService.setContentLanguage(contentLanguage)

      return patchResult
    },
    revokeMaintenancePeriod: async (context, data) => {
      if (!Object.hasOwn(data, 'id')) {
        context.commit('setErrorGetMaintenancePeriods', true)
        return Promise.resolve()
      }

      context.commit('setErrorGetMaintenancePeriods', false)
      context.commit('setLoading', true)

      const acceptedLanguage = AxiosRestService.resetAcceptedLanguage()
      const contentLanguage = AxiosRestService.resetContentLanguage()

      if (typeof data.revokedReason === 'string' && data.revokedReason.length > 0) {
        const payload = {
          data: {
            type: 'maintenancePeriods',
            id: data.id,
            attributes: {
              reason: data.reason,
              revokedReason: data.revokedReason,
            },
          },
        };

        await AxiosRestService.patch(
          `/maintenance-periods/${data.id}`,
          payload,
          {
            headers: {
              Accept: 'application/vnd.api+json',
              'Content-Type': 'application/vnd.api+json',
            },
          },
        )
          .catch(() => {
            context.commit('setErrorGetMaintenancePeriods', true)
          })
      }

      const deleteResult = AxiosRestService.delete(
        `/maintenance-periods/${data.id}`,
        { headers: { Accept: 'application/vnd.api+json' } },
      )
        .catch(() => {
          context.commit('setErrorGetMaintenancePeriods', true)
        })
        .finally(() => {
          context.commit('setLoading', false)
        })

      AxiosRestService.setAcceptedLanguage(acceptedLanguage)
      AxiosRestService.setContentLanguage(contentLanguage)

      return deleteResult
    },
  },
}
